import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { onValue, ref } from 'firebase/database'

import { db, tref } from '../lib/firebase.js'
import CLUB from '../config/club.js'
import { likePhoto } from '../lib/data.js'
import { hasLiked, toggleLiked, isFavorite, toggleFavorite } from '../lib/photoLocal.js'

// Permanent share target — /photo?t={tournamentId}&photo={photoId}. The TV
// "Find this" QR and the browse Share button both point here. Reads a single
// approved photo node; gracefully handles items removed by moderation.
export default function PhotoView() {
  const [params] = useSearchParams()
  const t = params.get('t')
  const photoId = params.get('photo')
  const [photo, setPhoto] = useState(undefined) // undefined=loading, null=gone
  const [teamNames, setTeamNames] = useState({})
  const [liked, setLiked] = useState(false)
  const [fav, setFav] = useState(false)

  useEffect(() => {
    if (!t || !photoId) { setPhoto(null); return }
    setLiked(hasLiked(t, photoId))
    setFav(isFavorite(t, photoId))
    const off = onValue(tref(`tournaments/${t}/photos/${photoId}`), (snap) => {
      const v = snap.val()
      setPhoto(v && v.status === 'approved' ? { id: photoId, ...v } : null)
    })
    return () => off()
  }, [t, photoId])

  // Resolve the team name lazily (one small read).
  useEffect(() => {
    if (!t || !photo?.teamId) return
    const off = onValue(tref(`tournaments/${t}/flights`), (snap) => {
      const flights = snap.val() || {}
      const map = {}
      for (const f of Object.values(flights)) {
        for (const [tid, tm] of Object.entries(f?.teams || {})) map[tid] = tm?.info?.name || tid
      }
      setTeamNames(map)
    })
    return () => off()
  }, [t, photo?.teamId])

  const onLike = () => {
    const wasLiked = liked
    toggleLiked(t, photoId)
    setLiked(!wasLiked)
    likePhoto(t, photoId, wasLiked ? -1 : 1).catch(() => {})
  }
  const onFav = () => setFav(isFavoriteAfter(toggleFavorite(t, photoId), photoId))
  const onShare = async () => {
    const url = window.location.href
    try {
      if (navigator.share) await navigator.share({ url, title: 'Tournament photo' })
      else { await navigator.clipboard?.writeText(url); alert('Link copied') }
    } catch { /* user cancelled */ }
  }

  if (photo === undefined) return <Shell><p className="text-white/60">Loading…</p></Shell>
  if (photo === null) return <Shell><p className="text-white/70">This photo is no longer available.</p></Shell>

  const teamName = photo.teamId ? (teamNames[photo.teamId] || null) : null
  const likeCount = Math.max(0, Number(photo.likeCount) || 0)

  return (
    <Shell>
      <div className="w-full max-w-3xl">
        <div className="flex items-center justify-center" style={{ maxHeight: '70vh' }}>
          {photo.type === 'video'
            ? <video src={photo.url} poster={photo.posterUrl || undefined} controls autoPlay playsInline className="max-w-full max-h-[70vh] object-contain rounded-lg" />
            : <img src={photo.url} alt={photo.caption || ''} className="max-w-full max-h-[70vh] object-contain rounded-lg" />}
        </div>
        {photo.caption ? <div className="font-bold text-lg mt-3">{photo.caption}</div> : null}
        <div className="text-sm text-white/70 flex flex-wrap gap-2 mt-1">
          <span>{photo.submittedBy || 'Guest'}</span>
          {teamName ? <span>· {teamName}</span> : null}
          {photo.hole != null ? <span>· Hole {photo.hole}</span> : null}
          <span>· {formatTime(photo.timestamp)}</span>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onLike} className="rounded-lg px-4 py-2 text-sm font-bold"
            style={liked ? { backgroundColor: CLUB.accentColor, color: CLUB.primaryColor } : { backgroundColor: '#ffffff18', color: '#fff' }}>
            ♥ {likeCount}
          </button>
          <button onClick={onFav} className="rounded-lg px-4 py-2 text-sm font-bold"
            style={fav ? { backgroundColor: CLUB.accentColor, color: CLUB.primaryColor } : { backgroundColor: '#ffffff18', color: '#fff' }}>
            {fav ? '★ Saved' : '☆ Save'}
          </button>
          <button onClick={onShare} className="rounded-lg px-4 py-2 text-sm font-bold" style={{ backgroundColor: '#ffffff18', color: '#fff' }}>
            ⤴ Share
          </button>
          <a href={t ? `/photowall?t=${t}` : '/'} className="ml-auto rounded-lg px-4 py-2 text-sm font-bold" style={{ color: CLUB.accentColor, border: `1px solid ${CLUB.accentColor}66` }}>
            All photos
          </a>
        </div>
      </div>
    </Shell>
  )
}

function isFavoriteAfter(list, id) { return Array.isArray(list) && list.includes(id) }

function Shell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 text-white" style={{ backgroundColor: '#0A1710' }}>
      {children}
    </div>
  )
}

function formatTime(ts) {
  if (!ts) return ''
  return new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
